import { motion } from "framer-motion";
import { Phone, ChevronDown } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1753791271758-42502f800d8f?auto=format&fit=crop&w=1920&q=80";

const TITLE = "Art meets Party";
const SUBTITLE = "— seit 1723.";

const letterVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.03, duration: 0.4, ease: "easeOut" },
  }),
};

export default function Hero() {
  return (
    <section
      className="relative w-full h-screen min-h-[600px] overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Atelier 77 – Festlich beleuchtete Partyscheune Bochum"
          className="ken-burns-img w-full h-full object-cover"
        />
        {/* Starker dunkler Overlay für Lesbarkeit */}
        <div className="absolute inset-0 bg-[#141414]/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#141414]/60 via-[#141414]/40 to-[#141414]" />
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-6 px-5 py-2 border border-[#D4A843]/60 bg-[#141414]/40 backdrop-blur-sm"
          data-testid="hero-badge"
        >
          <span
            className="text-xs tracking-[0.4em] uppercase text-[#D4A843]"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
          >
            Bochum-Laer · Künstlerhof
          </span>
        </motion.div>

        <h1
          className="leading-none mb-3 text-white"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 800,
            fontSize: "clamp(3.2rem, 9vw, 7.5rem)",
            textTransform: "uppercase",
            textShadow: "0 2px 20px rgba(0,0,0,0.8), 0 0 40px rgba(0,0,0,0.5)",
          }}
          data-testid="hero-title"
        >
          {TITLE.split("").map((char, i) => (
            <motion.span
              key={`t-${i}`}
              custom={i}
              variants={letterVariants}
              initial="hidden"
              animate="visible"
              className="inline-block"
            >
              {char === " " ? "\u00A0" : char}
            </motion.span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mb-6"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontSize: "clamp(1.6rem, 3.5vw, 3rem)",
            color: "#D4A843",
            textShadow: "0 2px 12px rgba(0,0,0,0.7)",
          }}
          data-testid="hero-subtitle"
        >
          {SUBTITLE}
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="text-white/75 text-base md:text-lg max-w-xl mb-10 font-light"
          style={{ fontFamily: "'Outfit', sans-serif", textShadow: "0 1px 8px rgba(0,0,0,0.8)" }}
          data-testid="hero-tagline"
        >
          Der Künstlerhof in Bochum-Laer. Einmieten. Einrichten. Feiern.
        </motion.p>

        <motion.a
          href="tel:02349350002"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.6 }}
          className="flex items-center gap-3 bg-[#C1440E] hover:bg-[#D4A843] text-white hover:text-[#141414] px-8 py-4 transition-all duration-300"
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 600,
            fontSize: "13px",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            textDecoration: "none",
          }}
          data-testid="hero-cta-btn"
        >
          <Phone size={16} />
          Jetzt anfragen – 0234 / 935 000 2
        </motion.a>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 1.0 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
        data-testid="scroll-indicator"
      >
        <span
          className="text-[#F2EDE8]/25 text-[10px] tracking-[0.3em] uppercase"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Entdecken
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown size={18} className="text-[#F2EDE8]/25" />
        </motion.div>
      </motion.div>
    </section>
  );
}
